import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Title,
  Text,
  Grid,
  Card,
  Badge,
  AreaChart,
  Select,
  SelectItem,
} from '@tremor/react';
import { Coins, AlertTriangle } from 'lucide-react';
import { KPICard } from '@/components/KPICard';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api } from '@/lib/api';

interface ModelCost {
  model_name: string | null;
  calls: number;
  success: number;
  error: number;
  estimated_brl: number;
  rate_brl_per_call: number;
}

interface DailyPoint {
  day: string;
  calls: number;
  estimated_brl: number;
}

interface ProcessoCost {
  processo_id: number;
  calls: number;
  distinct_models: number;
  estimated_brl: number;
}

interface AiCostSummary {
  period_days: number;
  total_calls: number;
  total_estimated_brl: number;
  by_model: ModelCost[];
  daily: DailyPoint[];
  instrumentation_warning: string;
}

interface RealModelo { provider: string; model: string; calls: number; tokens_in: number; tokens_out: number; usd: number }
interface RealOrigem { origem: string; calls: number; usd: number }
interface RealCost {
  days: number; total_usd: number; total_brl: number;
  por_modelo: RealModelo[]; por_origem: RealOrigem[];
  por_dia: { dia: string; calls: number; usd: number }[]; nota: string;
}

interface LlmLinha {
  provider: string; model: string; calls: number; share_pct?: number; aprox?: boolean;
  tokens_in: number | null; tokens_out: number | null; usd: number | null; brl: number | null;
}
interface ProcBreakdown {
  processo_id: number; fonte: 'real' | 'real_caso' | 'estimativa';
  total_usd: number | null; total_brl: number | null; por_llm: LlmLinha[]; nota?: string;
}

const fmtBrl = (n: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(n);

const fmtInt = (n: number) => new Intl.NumberFormat('pt-BR').format(n);

export function AICostPage() {
  const [days, setDays] = useState<number>(7);

  const summary = useQuery<AiCostSummary>({
    queryKey: ['ai-cost.summary', days],
    queryFn: async () =>
      (await api().get(`/ai-cost/summary?days=${days}`)).data,
  });

  const byProcesso = useQuery<ProcessoCost[]>({
    queryKey: ['ai-cost.by-processo', days],
    queryFn: async () =>
      (await api().get(`/ai-cost/by-processo?days=${days}`)).data,
  });

  const real = useQuery<RealCost>({
    queryKey: ['ai-cost.real', days],
    queryFn: async () => (await api().get(`/ai-cost/real?days=${days}`)).data,
  });

  // [03/07] clique no nº do processo (Top 20) → breakdown por LLM daquele caso
  const [openPid, setOpenPid] = useState<number | null>(null);
  const breakdown = useQuery<ProcBreakdown>({
    queryKey: ['ai-cost.processo', openPid],
    queryFn: async () => (await api().get(`/ai-cost/processo/${openPid}`)).data,
    enabled: openPid !== null,
  });

  if (summary.isLoading) return <LoadingSkeleton />;
  if (!summary.data) return <Text>Failed to load AI cost summary.</Text>;

  const s = summary.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title>AI Cost</Title>
          <Text>
            Custos estimados de chamadas LLM no pipeline de extração.
          </Text>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={days.toString()}
            onValueChange={(v) => setDays(Number(v))}
            className="w-32"
          >
            <SelectItem value="1">1 dia</SelectItem>
            <SelectItem value="7">7 dias</SelectItem>
            <SelectItem value="14">14 dias</SelectItem>
            <SelectItem value="30">30 dias</SelectItem>
          </Select>
          <Badge color="slate" icon={Coins}>
            estimativa
          </Badge>
        </div>
      </div>

      {/* Disclaimer */}
      <Card className="border border-yellow-700/40 bg-yellow-950/20">
        <div className="flex gap-2 text-sm text-yellow-200">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <div>{s.instrumentation_warning}</div>
        </div>
      </Card>

      {/* CUSTO REAL — llm_call_log (toda chamada: worker + juiz + jobs) */}
      {real.data && real.data.por_modelo.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <Title className="text-base">Custo REAL (tokens × preço) — todas as chamadas</Title>
            <Badge color="emerald">US$ {real.data.total_usd.toFixed(2)} · {fmtBrl(real.data.total_brl)}</Badge>
          </div>
          <Text className="text-xs mb-3">{real.data.nota}</Text>
          <Grid numItemsMd={2} className="gap-4">
            <div>
              <Text className="font-semibold text-sm mb-1">Por provedor/modelo</Text>
              <table className="w-full text-xs">
                <thead><tr className="text-left opacity-60"><th>modelo</th><th className="text-right">calls</th><th className="text-right">tokens</th><th className="text-right">US$</th></tr></thead>
                <tbody>
                  {real.data.por_modelo.map((m) => (
                    <tr key={m.provider + m.model} className="border-t border-slate-700/30">
                      <td className="py-1">{m.provider}:{m.model}</td>
                      <td className="text-right">{fmtInt(m.calls)}</td>
                      <td className="text-right">{fmtInt(m.tokens_in + m.tokens_out)}</td>
                      <td className="text-right">{m.usd.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <Text className="font-semibold text-sm mb-1">Por origem (worker / juiz / jobs)</Text>
              <table className="w-full text-xs">
                <thead><tr className="text-left opacity-60"><th>origem</th><th className="text-right">calls</th><th className="text-right">US$</th></tr></thead>
                <tbody>
                  {real.data.por_origem.map((o) => (
                    <tr key={o.origem} className="border-t border-slate-700/30">
                      <td className="py-1">{o.origem}</td>
                      <td className="text-right">{fmtInt(o.calls)}</td>
                      <td className="text-right">{o.usd.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Grid>
        </Card>
      )}

      {/* KPIs */}
      <Grid numItemsMd={3} className="gap-4">
        <KPICard
          label={`Calls · ${s.period_days}d`}
          value={fmtInt(s.total_calls)}
        />
        <KPICard
          label="Custo estimado"
          value={fmtBrl(s.total_estimated_brl)}
        />
        <KPICard
          label="Por dia"
          value={fmtBrl(s.total_estimated_brl / Math.max(1, s.period_days))}
        />
      </Grid>

      {/* Daily series */}
      <Card>
        <Title>Custo diário</Title>
        {s.daily.length === 0 ? (
          <Text>Sem dados no período.</Text>
        ) : (
          <AreaChart
            className="mt-4 h-64"
            data={s.daily}
            index="day"
            categories={['estimated_brl']}
            colors={['blue']}
            valueFormatter={(v) => fmtBrl(v)}
            showAnimation
          />
        )}
      </Card>

      {/* By model */}
      <Card>
        <Title>Por modelo</Title>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-400">
              <tr>
                <th className="pb-2">Modelo</th>
                <th className="pb-2 text-right">Calls</th>
                <th className="pb-2 text-right">Sucesso</th>
                <th className="pb-2 text-right">Erro</th>
                <th className="pb-2 text-right">R$/call</th>
                <th className="pb-2 text-right">R$ total</th>
              </tr>
            </thead>
            <tbody>
              {s.by_model.map((m) => (
                <tr
                  key={m.model_name ?? '_'}
                  className="border-t border-slate-800/60"
                >
                  <td className="py-2 font-mono text-xs">
                    {m.model_name ?? '(unknown)'}
                  </td>
                  <td className="py-2 text-right">{fmtInt(m.calls)}</td>
                  <td className="py-2 text-right text-emerald-400">
                    {fmtInt(m.success)}
                  </td>
                  <td className="py-2 text-right text-red-400">
                    {fmtInt(m.error)}
                  </td>
                  <td className="py-2 text-right text-slate-400">
                    {fmtBrl(m.rate_brl_per_call)}
                  </td>
                  <td className="py-2 text-right">
                    {fmtBrl(m.estimated_brl)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Top processos */}
      <Card>
        <Title>Top 20 processos por consumo</Title>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-400">
              <tr>
                <th className="pb-2">Processo</th>
                <th className="pb-2 text-right">Calls</th>
                <th className="pb-2 text-right">Modelos</th>
                <th className="pb-2 text-right">Custo</th>
              </tr>
            </thead>
            <tbody>
              {(byProcesso.data ?? []).map((p) => (
                <>
                  <tr
                    key={p.processo_id}
                    className="border-t border-slate-800/60"
                  >
                    <td className="py-2">
                      <button
                        className="text-blue-400 underline decoration-dotted underline-offset-4 hover:text-blue-300"
                        onClick={() =>
                          setOpenPid(openPid === p.processo_id ? null : p.processo_id)
                        }
                        title="ver breakdown por LLM"
                      >
                        #{p.processo_id} {openPid === p.processo_id ? '▾' : '▸'}
                      </button>
                    </td>
                    <td className="py-2 text-right">{fmtInt(p.calls)}</td>
                    <td className="py-2 text-right text-slate-400">
                      {p.distinct_models}
                    </td>
                    <td className="py-2 text-right">{fmtBrl(p.estimated_brl)}</td>
                  </tr>
                  {openPid === p.processo_id && (
                    <tr key={`${p.processo_id}-bd`}>
                      <td colSpan={4} className="pb-3">
                        <div className="ml-6 mt-1 rounded-lg border border-slate-700/60 bg-slate-900/40 p-3">
                          {breakdown.isLoading && (
                            <span className="text-xs text-slate-400">carregando breakdown…</span>
                          )}
                          {breakdown.data && (
                            <>
                              <div className="mb-2 flex items-center gap-2 text-xs">
                                <span className="font-semibold">
                                  Breakdown por LLM — #{breakdown.data.processo_id}
                                </span>
                                <span className={breakdown.data.fonte === 'real'
                                  ? 'rounded bg-emerald-900/60 px-2 py-0.5 text-emerald-300'
                                  : breakdown.data.fonte === 'real_caso'
                                    ? 'rounded bg-sky-900/60 px-2 py-0.5 text-sky-300'
                                    : 'rounded bg-amber-900/60 px-2 py-0.5 text-amber-300'}>
                                  {breakdown.data.fonte === 'real'
                                    ? 'custo real (tokens × preço, por chamada)'
                                    : breakdown.data.fonte === 'real_caso'
                                      ? 'custo real do caso (total medido; pré-coleta por chamada)'
                                      : 'sem medição confiável'}
                                </span>
                                <span className="ml-auto font-semibold">
                                  {breakdown.data.total_brl != null ? fmtBrl(breakdown.data.total_brl) : '—'}
                                  {breakdown.data.total_usd != null &&
                                    ` · US$ ${breakdown.data.total_usd.toFixed(3)}`}
                                </span>
                              </div>
                              <table className="w-full text-xs">
                                <thead className="text-left text-slate-500">
                                  <tr>
                                    <th className="pb-1">LLM</th>
                                    <th className="pb-1 text-right">calls</th>
                                    <th className="pb-1 text-right">tokens in</th>
                                    <th className="pb-1 text-right">tokens out</th>
                                    <th className="pb-1 text-right">US$</th>
                                    <th className="pb-1 text-right">R$</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {breakdown.data.por_llm.map((l) => (
                                    <tr key={l.provider + l.model} className="border-t border-slate-800/60">
                                      <td className="py-1 font-mono">{l.provider}:{l.model}</td>
                                      <td className="py-1 text-right">{fmtInt(l.calls)}{l.share_pct != null ? ` (${l.share_pct}%)` : ''}</td>
                                      <td className="py-1 text-right">{l.tokens_in != null ? fmtInt(l.tokens_in) : '—'}</td>
                                      <td className="py-1 text-right">{l.tokens_out != null ? fmtInt(l.tokens_out) : '—'}</td>
                                      <td className="py-1 text-right">{l.usd != null ? `${l.aprox ? '≈' : ''}${l.usd.toFixed(4)}` : '—'}</td>
                                      <td className="py-1 text-right">{l.brl != null ? `${l.aprox ? '≈' : ''}${fmtBrl(l.brl)}` : '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {breakdown.data.nota && (
                                <div className="mt-2 text-[11px] text-slate-500">{breakdown.data.nota}</div>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
              {(!byProcesso.data || byProcesso.data.length === 0) && (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-slate-500">
                    Sem dados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
